import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import { Control, Controller } from "react-hook-form";
import {
  Label,
  Paragraph,
  Input as TInput,
  InputProps as TInputProps,
  XStack,
  YStack,
} from "tamagui";
import { SubmitButton } from "./buttons/submit-button";

export interface TamaguiInputProps extends TInputProps {
  label?: string;
  helperText?: string;
  error?: string;
  fullWidth?: boolean;
  inputType?: "text" | "password" | "email" | "number";
  showPasswordToggle?: boolean;
}

const TamaguiInput = React.forwardRef<any, TamaguiInputProps>(
  (
    {
      label,
      helperText,
      error,
      fullWidth,
      inputType = "text",
      showPasswordToggle = true,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    // Show password toggle only when type is password
    const shouldShowToggle = inputType === "password" && showPasswordToggle;

    // For password fields, use secureTextEntry
    const inputProps = {
      ...props,
      secureTextEntry: inputType === "password" && !showPassword,
    };

    // If the type is email or number, add the keyboardType
    if (inputType === "email") {
      inputProps.keyboardType = "email-address";
      inputProps.autoCapitalize = "none";
    } else if (inputType === "number") {
      inputProps.keyboardType = "numeric";
    }

    return (
      <YStack width={fullWidth ? "100%" : undefined} gap="$2">
        {label && <Label htmlFor={props.id}>{label}</Label>}
        <XStack width="100%" alignItems="center">
          <TInput
            ref={ref}
            borderWidth={1}
            padding="$2"
            borderColor={error ? "$red500" : "$terracotta500"}
            flex={1}
            focusStyle={{
              borderColor: "$terracotta600",
              borderWidth: 2,
              outlineWidth: 0,
              backgroundColor: "transparent",
            }}
            {...inputProps}
          />
          {shouldShowToggle && (
            <SubmitButton
              iconOnly={true}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowPassword(!showPassword);
              }}
              color="terracotta"
              size="sm"
              className="ml-2"
              icon={
                showPassword ? (
                  <EyeIcon className="w-4 h-4 text-cream-300" />
                ) : (
                  <EyeSlashIcon className="w-4 h-4 text-cream-300" />
                )
              }
            />
          )}
        </XStack>
        {(helperText || error) && (
          <Paragraph size="$2" color={error ? "$red500" : "$terracotta600"}>
            {error || helperText}
          </Paragraph>
        )}
      </YStack>
    );
  }
);

TamaguiInput.displayName = "TamaguiInput";

export function TamaguiInputWithController({
  control,
  name,
  ...props
}: {
  control: Control<any>;
  name: string;
  props: TamaguiInputProps;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, onBlur } }) => (
        <TamaguiInput
          {...props}
          value={value}
          onChangeText={onChange}
          onBlur={onBlur}
        />
      )}
    />
  );
}

export default TamaguiInput;
